import { Controller } from '@hotwired/stimulus'
import { Modal, Tooltip } from 'bootstrap'
import { openModal, setFrameSrc } from '../../helpers/custom_events_helper'

export default class extends Controller {
  static targets = [
    'bulkUpdateButton',
    'serviceRecords',
    'bulkUpdateButtonError',
    'bulkDeleteButton',
    'bulkManagementActionsContainer',
    'bulkManagementActionsContainerSummary',
    'bulkManagementActionsContainerSummaryCount',
    'bulkManagementActionsContainerSummaryText',
    'exportSelectedButton',
    'exportButton',
    'selectAllPagesLink',
    'allPagesSelectedText',
    'allPagesDeselectLink'
  ]

  static outlets = ['ui--table']

  static values = {
    deleteUrl: String,
    exportModalUrl: String,
    exportUrl: String,
    currentPageRecords: Number,
    totalRecords: Number,
    bulkEditSessionUrl: String,
    confirmBulkEditSessionUrl: String,
    ransackQuery: String
  }

  connect() {
    this.selection = []
    this.serviceRecordSelected = false
    this.allPagesMode = false
    if (this.hasBulkUpdateButtonTarget) {
      this.defaultBulkEditButtonLabel = this.bulkUpdateButtonTarget.text
    }
    if (this.hasBulkDeleteButtonTarget) {
      this.defaultBulkDeleteButtonLabel = this.bulkDeleteButtonTarget.text
    }
    this.liveRegion = document.getElementById('live-region-polite')
  }

  uiTableOutletConnected(outlet) {
    this.selection = outlet.selectionData
    outlet.addSelectionChangeListener((event) => {
      this.selection = event.detail.selection
      this.refreshBulkActionButtons()
    })
    this.refreshBulkActionButtons()
  }

  // Legacy DOM-driven selection for pages that haven't been migrated to UI::Table::ConfigComponent
  // (e.g. providers/statements). Migrated pages (service_records) use the ui--table outlet above.
  toggleCheckBoxes() {
    if (!this.hasServiceRecordsTarget) return

    this.serviceRecordSelected = !this.serviceRecordSelected
    const checkboxes = this.serviceRecordsTarget.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach((el) => (el.checked = this.serviceRecordSelected))

    this.toggleBulkActionButtons()
  }

  toggleBulkActionButtons() {
    if (!this.hasServiceRecordsTarget) return

    const selectedServiceRecords = Array.from(
      this.serviceRecordsTarget.querySelectorAll('input.service-record-checkbox:checked')
    )

    this.selection = selectedServiceRecords.map((checkbox) => ({
      id: parseInt(checkbox.value, 10),
      program: checkbox.parentElement?.parentElement?.dataset?.program
    }))

    this.refreshBulkActionButtons()
  }

  // reveals or hides buttons to bulk edit / delete selected service records based on current selection
  refreshBulkActionButtons() {
    if (this.allPagesMode) {
      this.showAllPagesUi()
      return
    }

    const selectedCount = this.selection.length

    if (selectedCount > 0) {
      const firstProgram = this.selection[0].program
      const allMatchingProgram = this.selection.every((record) => record.program === firstProgram)
      const tooManyRecords = selectedCount > 250

      if (allMatchingProgram && !tooManyRecords) {
        if (this.hasBulkUpdateButtonErrorTarget) {
          this.hide(this.bulkUpdateButtonErrorTarget.parentElement)
        }
        if (this.hasBulkUpdateButtonTarget) {
          this.reveal(this.bulkUpdateButtonTarget)
          this.setSummaryCountandTextFor(selectedCount)
          this.reveal(this.bulkManagementActionsContainerSummaryTarget)
        }
      } else {
        if (this.hasBulkUpdateButtonTarget) {
          this.hide(this.bulkUpdateButtonTarget)
        }
        if (this.hasBulkUpdateButtonErrorTarget) {
          let tooltipMessage
          if (tooManyRecords) {
            tooltipMessage = 'Bulk editing is disabled when more than 250 records are selected. Please reduce to 250 or fewer to enable bulk editing.'
          } else {
            tooltipMessage = 'Filter your service records down to a single program to enable editing'
          }

          const tooltipWrapper = this.bulkUpdateButtonErrorTarget.parentElement

          tooltipWrapper.setAttribute('data-bs-title', tooltipMessage)
          tooltipWrapper.setAttribute('data-bs-original-title', tooltipMessage)

          const tooltipInstance = Tooltip.getInstance(tooltipWrapper)
          if (tooltipInstance) {
            tooltipInstance.dispose()
            new Tooltip(tooltipWrapper, {
              title: tooltipMessage
            })
          }

          this.reveal(this.bulkUpdateButtonErrorTarget.parentElement)
          this.setSummaryCountandTextFor(selectedCount)
          this.reveal(this.bulkManagementActionsContainerSummaryTarget)
        }
      }

      // Show "select all pages" link only when at least one filter is set and there are
      // more records across pages than currently selected.
      if (this.hasSelectAllPagesLinkTarget && !this.allPagesMode && this.hasActiveFilters() && this.totalRecordsValue > selectedCount) {
        this.reveal(this.selectAllPagesLinkTarget)
      } else if (this.hasSelectAllPagesLinkTarget) {
        this.hide(this.selectAllPagesLinkTarget)
      }

      if (this.hasBulkDeleteButtonTarget) {
        this.reveal(this.bulkDeleteButtonTarget)
        this.setSummaryCountandTextFor(selectedCount)
      }

      if (this.hasBulkManagementActionsContainerTarget) {
        this.reveal(this.bulkManagementActionsContainerTarget)
      }
    } else {
      this.liveRegion.textContent = 'No service records are selected'
      if (this.hasBulkUpdateButtonTarget) {
        this.hide(this.bulkUpdateButtonTarget)
      }

      if (this.hasBulkDeleteButtonTarget) {
        this.hide(this.bulkDeleteButtonTarget)
      }

      if (this.hasBulkUpdateButtonErrorTarget) {
        this.hide(this.bulkUpdateButtonErrorTarget.parentElement)
      }

      if (this.hasBulkManagementActionsContainerTarget) {
        this.hide(this.bulkManagementActionsContainerTarget)
      }

      if (this.hasSelectAllPagesLinkTarget) this.hide(this.selectAllPagesLinkTarget)
      if (this.hasAllPagesSelectedTextTarget) this.hide(this.allPagesSelectedTextTarget)
      if (this.hasAllPagesDeselectLinkTarget) this.hide(this.allPagesDeselectLinkTarget)
      if (this.hasExportSelectedButtonTarget) this.reveal(this.exportSelectedButtonTarget)
      this.bulkManagementActionsContainerTarget?.classList.remove('bulk-management-actions--all-pages')
      this.allPagesMode = false
    }
  }

  reveal(element) {
    element.classList.remove('d-none')
  }

  hide(element) {
    element.classList.add('d-none')
  }

  setSummaryCountandTextFor = (selectedCount) => {
    this.bulkManagementActionsContainerSummaryCountTarget.innerHTML = selectedCount
    const followText = selectedCount === 1 ? 'service record on this page is selected' : 'service records on this page are selected'
    this.bulkManagementActionsContainerSummaryTextTarget.innerHTML = `&nbsp;${followText}`
    this.liveRegion.textContent = selectedCount + followText
  }

  // "Select all pages" edits/deletes every record matching the current filters via a
  // ransack query, independent of the rows selected on the current page. It also checks
  // every row on the current page so the table visibly reflects the selection.
  selectAllPages(event) {
    event.preventDefault()
    this.allPagesMode = true

    if (this.hasUiTableOutlet) this.uiTableOutlet.selectAll()
    this.showAllPagesUi()
  }

  showAllPagesUi() {
    if (this.hasSelectAllPagesLinkTarget) this.hide(this.selectAllPagesLinkTarget)
    if (this.hasAllPagesSelectedTextTarget) this.reveal(this.allPagesSelectedTextTarget)
    if (this.hasAllPagesDeselectLinkTarget) this.reveal(this.allPagesDeselectLinkTarget)
    if (this.hasBulkManagementActionsContainerSummaryTarget) this.hide(this.bulkManagementActionsContainerSummaryTarget)
    if (this.hasExportSelectedButtonTarget) this.hide(this.exportSelectedButtonTarget)

    if (this.hasBulkUpdateButtonTarget) this.reveal(this.bulkUpdateButtonTarget)
    if (this.hasBulkUpdateButtonErrorTarget) this.hide(this.bulkUpdateButtonErrorTarget.parentElement)
    if (this.hasBulkDeleteButtonTarget) this.reveal(this.bulkDeleteButtonTarget)
    if (this.hasBulkManagementActionsContainerTarget) {
      this.reveal(this.bulkManagementActionsContainerTarget)
      this.bulkManagementActionsContainerTarget.classList.add('bulk-management-actions--all-pages')
    }
  }

  clearAllPagesSelection(event) {
    event.preventDefault()
    this.allPagesMode = false

    if (this.hasUiTableOutlet) this.uiTableOutlet.deselectAll()
    this.selection = []

    if (this.hasAllPagesSelectedTextTarget) this.hide(this.allPagesSelectedTextTarget)
    if (this.hasAllPagesDeselectLinkTarget) this.hide(this.allPagesDeselectLinkTarget)
    this.refreshBulkActionButtons()
  }

  // sends request which will pop a modal for bulk updating fields on selected service records
  updateSelectedServiceRecords(event) {
    event.preventDefault()

    if (this.allPagesMode) {
      this.openConfirmBulkEditSessionModal()
    } else {
      this.openConfirmBulkEditSessionModal(this.selection.map((record) => record.id))
    }
  }

  // sends request which will pop a modal for bulk deleting selected service records
  deleteSelectedServiceRecords(event) {
    event.preventDefault()

    if (this.allPagesMode) {
      this.openConfirmBulkDeleteModal()
    } else {
      this.openModalWithSelectedServiceRecord(this.deleteUrlValue)
    }
  }

  exportServiceRecords(event) {
    event.preventDefault()

    this.openModalWithSelectedServiceRecord(this.exportModalUrlValue, false)
  }

  exportSelectedServiceRecords(event) {
    event.preventDefault()

    if (this.allPagesMode) {
      this.exportAllMatchingRecords()
    } else {
      this.openModalWithSelectedServiceRecord(this.exportModalUrlValue)
    }
  }

  exportAllMatchingRecords() {
    const baseURL = new URL(this.exportUrlValue, window.location.origin)
    baseURL.searchParams.set('export_type', 'full')

    const link = document.createElement('a')
    link.href = baseURL.pathname + baseURL.search
    link.download = 'service_records.csv'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  // Load the confirm modal by submitting a hidden form (POST) rather than GET-loading the
  // frame via `src`, so the selected ids ride in the request body. A large selection would
  // otherwise overflow the URL length limit and fail with a 414. The form targets the modal's
  // turbo-frame, so Turbo renders the response into the (just-opened) dialog.
  openConfirmBulkEditSessionModal(selectedIds = null) {
    const modalEl = document.getElementById('bulk-edit-confirm-container')
    const form = document.getElementById('bulk-edit-confirm-form')
    if (!modalEl || !form) return

    form.querySelectorAll('[data-selection-input]').forEach((input) => input.remove())

    const params = selectedIds && selectedIds.length > 0
      ? selectedIds.map((id) => ['q[id_in][]', id])
      : this.ransackQueryParams()
    params.forEach(([name, value]) => form.appendChild(this.selectionInput(name, value)))

    openModal(modalEl)
    // openModal dispatches asynchronously; defer the submit to the next task so the dialog
    // and its turbo-frame exist when Turbo resolves the form's target.
    setTimeout(() => form.requestSubmit(), 0)
  }

  selectionInput(name, value) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    input.dataset.selectionInput = 'true'
    return input
  }

  openConfirmBulkDeleteModal() {
    const baseUrl = new URL(this.deleteUrlValue, window.location.origin)
    baseUrl.searchParams.set('scope', 'all_pages')
    this.appendRansackQuery(baseUrl)

    const modalEl = document.getElementById('bulk-delete-confirm-container')
    if (!modalEl) return

    openModal(modalEl)
    setFrameSrc(modalEl, baseUrl.toString())
  }

  appendRansackQuery(url) {
    this.ransackQueryParams().forEach(([name, value]) => url.searchParams.append(name, value))
  }

  // Flattens the current ransack query into an array of [name, value] pairs suitable for
  // either URL search params or hidden form fields.
  ransackQueryParams() {
    const ransackQuery = this.currentRansackQuery()
    const params = []
    for (const [key, value] of Object.entries(ransackQuery)) {
      if (Array.isArray(value)) {
        value.forEach((v) => params.push([`q[${key}][]`, v]))
      } else {
        params.push([`q[${key}]`, value])
      }
    }
    return params
  }

  openModalWithSelectedServiceRecord(modalUrl, selectionWarning = true) {
    const selectedIds = this.selection.map((record) => record.id)

    if (selectedIds.length > 0 || !selectionWarning) {
      var baseURL = new URL(modalUrl)
      var searchParams = new URLSearchParams(baseURL.search)

      if (selectedIds.length > 0) {
        selectedIds.forEach((id) => searchParams.append('ids[]', id))
        searchParams.append('selected_count', selectedIds.length)
      }

      searchParams.append('current_page_count', this.currentPageRecordsValue)
      searchParams.append('total_count', this.totalRecordsValue)

      const url = baseURL.pathname + '?' + searchParams

      if (url.length >= 10240) {
        alert(`Too many service records selected.`)
      } else {
        document.getElementById('modal').src = url
      }
    } else {
      document.dispatchEvent(
        new CustomEvent('toast', { detail: { failure: true, message: 'No service records selected' } })
      )
    }
  }

  createBulkEditSession(selectionParams) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.bulkEditSessionUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'text/html'
      },
      body: JSON.stringify({ bulk_edit_session: selectionParams }),
      redirect: 'follow'
    }).then((response) => {
      if (response.ok) {
        window.location.href = response.url
      }
    })
  }

  // True when the user has at least one filter applied. The ransack sort key ('s') is not a
  // filter, so it's ignored, as are any blank values.
  hasActiveFilters() {
    const query = this.currentRansackQuery()
    return Object.entries(query).some(([key, value]) => {
      if (key === 's') return false
      if (Array.isArray(value)) return value.some((v) => v !== null && v !== '')
      return value !== null && value !== ''
    })
  }

  currentRansackQuery() {
    if (this.ransackQueryValue) {
      try {
        return JSON.parse(this.ransackQueryValue)
      } catch (_e) {
        // fall through to URL parsing
      }
    }

    const urlParams = new URLSearchParams(window.location.search)
    const query = {}
    for (const [key, value] of urlParams) {
      if (!value) continue
      const match = key.match(/^q\[(.+)\]$/)
      if (!match) continue

      const paramName = match[1]
      if (key.endsWith('[]')) {
        const arrayName = paramName.replace(/\[\]$/, '')
        query[arrayName] = query[arrayName] || []
        query[arrayName].push(value)
      } else {
        query[paramName] = value
      }
    }
    return query
  }

  export(event) {
    event.preventDefault()

    let exportTypeRadioButton = document.querySelector('input[name="export_type"]:checked')
    let exportType = exportTypeRadioButton ? exportTypeRadioButton.value : null

    if (exportType) {
      var baseURL = new URL(this.exportUrlValue)
      var searchParams = new URLSearchParams(baseURL.search)

      searchParams.append('export_type', exportType)

      this.downloadCSV(baseURL, searchParams)
    } else {
      alert('Please specify which rows you want to export.')
    }
  }

  downloadCSV(baseURL, searchParams) {
    const url = baseURL.pathname + '?' + searchParams

    let csvDownloadLink = document.getElementById('download-csv-link')
    csvDownloadLink.href = url
    csvDownloadLink.click()

    Modal.getOrCreateInstance('#modal').hide()
  }
}
